import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { formatCurrency, formatDate } from '../../lib/formatters.js';
import { SettlementMethodPicker } from './components/SettlementMethodPicker.js';

interface QueueClaim {
  id: string;
  claimNumber: string;
  claimType: string;
  approvedAmount: number | null;
  isoCurrencyCode: string;
  distributorName: string;
  approvedAt: string;
  daysSinceApproval: number;
}

export function SettlementQueuePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [claims, setClaims] = useState<QueueClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    api.get<{ claims: QueueClaim[] }>('/settlement/queue')
      .then((r) => setClaims(r.claims))
      .catch(() => setClaims([]))
      .finally(() => setLoading(false));
  }, []);

  function toggleAll() {
    if (selected.size === claims.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(claims.map((c) => c.id)));
    }
  }

  function toggleOne(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  const selectedClaims = claims.filter((c) => selected.has(c.id));
  const totalByCurrency: Record<string, number> = {};
  for (const c of selectedClaims) {
    if (c.approvedAmount != null) {
      totalByCurrency[c.isoCurrencyCode] = (totalByCurrency[c.isoCurrencyCode] ?? 0) + c.approvedAmount;
    }
  }

  function handleBatchSuccess(batchId: string) {
    setShowPicker(false);
    navigate(`/approver/settlement/batches/${batchId}`);
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <span className="inline-block text-[11px] font-semibold tracking-[0.22em] uppercase text-copper mb-2">
          {t('settlement.queue.eyebrow')}
        </span>
        <h1 className="font-serif italic text-3xl text-ink">{t('settlement.queue.title')}</h1>
        <p className="text-slate-500 text-sm mt-1">{t('settlement.queue.subtitle')}</p>
      </div>

      {/* Batch action panel */}
      {selected.size > 0 && (
        <div className="p-4 bg-copper/5 border border-copper/30 rounded flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm font-semibold text-ink">
              {t('settlement.queue.selectedCount', { count: selected.size })}
            </p>
            <p className="text-xs text-slate-600 mt-0.5">
              {Object.entries(totalByCurrency).map(([cur, amt]) => formatCurrency(amt, cur)).join(', ')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowPicker(true)}
            className="px-5 py-2 bg-copper text-bone-soft rounded text-sm font-semibold hover:bg-copper-warm transition-colors"
          >
            {t('settlement.queue.generateBatch')}
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-slate-400 text-sm py-8">{t('common.loading')}</p>
      ) : claims.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <p className="text-3xl mb-2">✓</p>
          <p className="text-sm">{t('settlement.queue.empty')}</p>
        </div>
      ) : (
        <div className="border border-hairline rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-bone">
              <tr className="text-xs uppercase tracking-wide text-slate-500 border-b border-hairline">
                <th className="p-3 w-8">
                  <input
                    type="checkbox"
                    checked={selected.size === claims.length && claims.length > 0}
                    onChange={toggleAll}
                    className="accent-copper"
                  />
                </th>
                <th className="text-left p-3">{t('settlement.table.claimNumber')}</th>
                <th className="text-left p-3">{t('settlement.table.distributor')}</th>
                <th className="text-right p-3">{t('settlement.table.approvedAmount')}</th>
                <th className="text-left p-3">{t('settlement.table.claimType')}</th>
                <th className="text-left p-3">{t('settlement.table.approvedDate')}</th>
                <th className="text-right p-3">{t('settlement.table.daysSince')}</th>
              </tr>
            </thead>
            <tbody>
              {claims.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => toggleOne(c.id)}
                  className={['border-b border-hairline last:border-0 cursor-pointer transition-colors', selected.has(c.id) ? 'bg-copper/5' : 'hover:bg-bone'].join(' ')}
                >
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggleOne(c.id)}
                      className="accent-copper"
                    />
                  </td>
                  <td className="p-3 font-mono text-xs text-copper">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); navigate(`/claims/${c.id}`); }}
                      className="hover:underline"
                    >
                      {c.claimNumber}
                    </button>
                  </td>
                  <td className="p-3 text-xs">{c.distributorName}</td>
                  <td className="p-3 text-right font-mono text-xs">
                    {c.approvedAmount != null ? formatCurrency(c.approvedAmount, c.isoCurrencyCode) : '—'}
                  </td>
                  <td className="p-3 text-xs">
                    {t(`wizard.claimTypes.${c.claimType}.label`, { defaultValue: c.claimType })}
                  </td>
                  <td className="p-3 text-xs text-slate-500">{formatDate(c.approvedAt, 'short')}</td>
                  <td className={['p-3 text-right font-mono text-xs', c.daysSinceApproval > 5 ? 'text-danger' : 'text-slate-500'].join(' ')}>
                    {c.daysSinceApproval}d
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showPicker && (
        <SettlementMethodPicker
          selectedClaims={selectedClaims}
          onSuccess={handleBatchSuccess}
          onCancel={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}
